import { useState } from "react";
import { TextArea, Checkbox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { useAddDealNote, useDealNotes } from "src/dm/hooks/useDealNotes";
import * as s from "./DealNotes.css";

const fmtWhen = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

interface Props {
  dealId: number;
}

const DealNotes = ({ dealId }: Props) => {
  const { data, isLoading, isError, error } = useDealNotes(dealId);
  const add = useAddDealNote(dealId);
  const [text, setText] = useState("");
  const [internal, setInternal] = useState(false);

  const handleAdd = async () => {
    const noteText = text.trim();
    if (!noteText) return;
    await add.mutateAsync({ noteText, isInternal: internal });
    setText("");
    setInternal(false);
  };

  return (
    <section className={s.card}>
      <h2 className={s.title}>Notes</h2>

      {isLoading && <p className={s.empty}>Loading notes…</p>}
      {isError && (
        <p style={{ color: "#b91c1c" }}>
          Could not load notes: {error instanceof Error ? error.message : "unknown"}
        </p>
      )}
      {!isLoading && !isError && (data?.length ?? 0) === 0 && (
        <p className={s.empty}>No notes on this deal yet.</p>
      )}

      {data?.map((n) => (
        <div key={n.dealNoteId} className={s.noteItem}>
          <div className={s.noteMeta}>
            <span>#{n.dealNoteId}</span>
            <span>{n.whoCreated ?? "—"}</span>
            <span>{fmtWhen(n.whenCreated)}</span>
            {n.isInternal && <span className={s.internalBadge}>Internal</span>}
          </div>
          <div className={s.noteBody}>{n.noteText ?? ""}</div>
        </div>
      ))}

      <div className={s.composer}>
        <TextArea
          rows={3}
          placeholder="Add a note…"
          value={text}
          onChange={(e) => setText(String(e.value ?? ""))}
          maxLength={2000}
        />
        <div className={s.composerRow}>
          <Checkbox
            label="Internal only"
            checked={internal}
            onChange={(e) => setInternal(!!e.value)}
          />
          <Button
            type="button"
            themeColor="primary"
            onClick={handleAdd}
            disabled={add.isPending || !text.trim()}
          >
            {add.isPending ? "Saving…" : "Add note"}
          </Button>
        </div>
        {add.isError && (
          <div style={{ color: "#b91c1c", fontSize: "0.85rem" }}>
            Failed to save note:{" "}
            {add.error instanceof Error ? add.error.message : "unknown"}
          </div>
        )}
      </div>
    </section>
  );
};

export default DealNotes;
